import React, { useId, useMemo } from 'react';
import { Box, keyframes } from '@mui/material';

const pulseSquare = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: var(--grid-square-opacity, 0.22); }
`;

const beamTravel = keyframes`
  0% { offset-distance: 0%; }
  100% { offset-distance: 100%; }
`;

export default function AnimatedGridPattern({
  width = 48,
  height = 48,
  numSquares = 36,
  maxOpacity = 0.22,
  duration = 2.2,
  repeatDelay = 0.18,
  stagger = 0.045,
  strokeColor = 'rgba(76,225,182,0.1)',
  fillColor = 'rgba(76,225,182,0.45)',
  sx,
}) {
  const patternId = useId();

  const squares = useMemo(
    () =>
      Array.from({ length: numSquares }, (_, index) => ({
        id: index,
        left: `${((index * 17 + 7) * 13) % 91}%`,
        top: `${((index * 23 + 11) * 11) % 86}%`,
        delay: index * stagger + repeatDelay,
        cycle: duration + (index % 5) * 0.25,
      })),
    [duration, numSquares, repeatDelay, stagger]
  );

  return (
    <Box
      aria-hidden
      sx={{
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box component="svg" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <pattern id={patternId} width={width} height={height} patternUnits="userSpaceOnUse">
            <path d={`M.5 ${height}V.5H${width}`} fill="none" stroke={strokeColor} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </Box>

      {squares.map((square) => (
        <Box
          key={square.id}
          sx={{
            position: 'absolute',
            left: square.left,
            top: square.top,
            width: width - 1,
            height: height - 1,
            bgcolor: fillColor,
            '--grid-square-opacity': maxOpacity,
            opacity: 0,
            animation: `${pulseSquare} ${square.cycle}s ease-in-out ${square.delay}s infinite alternate`,
          }}
        />
      ))}
    </Box>
  );
}

export function BorderBeam({
  size = 120,
  duration = 10,
  colorFrom = '#4ce1b6',
  colorTo = '#22d3ee',
  borderRadius = 'inherit',
}) {
  return (
    <Box
      aria-hidden
      sx={{
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        borderRadius,
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius,
          padding: '1px',
          background: `linear-gradient(135deg, ${colorFrom}55, transparent 40%, transparent 60%, ${colorTo}55)`,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          opacity: 0.85,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colorFrom} 0%, ${colorTo} 45%, transparent 70%)`,
          filter: 'blur(8px)',
          offsetPath: `rect(0 auto auto 0 round ${Math.min(size, 48)}px)`,
          WebkitOffsetPath: `rect(0 auto auto 0 round ${Math.min(size, 48)}px)`,
          offsetRotate: '0deg',
          animation: `${beamTravel} ${duration}s linear infinite`,
        }}
      />
    </Box>
  );
}
